'use strict';

/**
 * T901 D5 step 3 — browser-side password re-wrap of the identity vault.
 *
 * ── WHAT THIS IS, AND WHAT IT DELIBERATELY IS NOT ───────────────────────────
 * A password change on a vault account is NOT a content-key rotation.
 * `identityMaster` is a stable INNER key: `meta.wrapPw` and `meta.wrapSeed`
 * both wrap the SAME `identityMaster` (`shared/vaults/identity-vault.js`), so
 * changing the password means re-wrapping that one key under a key derived
 * from the new password. Every DEK, every module master and every sealed
 * record is untouched.
 *
 * ⇒ This module contains NO `deriveMasterKey` and MUST NOT grow one.
 *   `BLK-T901-P7-client-half-no-browser-derivation-20260809` UNBLOCK-PATH (a)
 *   ("export deriveMasterKey from the vault bundle") is STRUCK: the SEAMS
 *   browser twin `shared/crypto/browser-master-key.js` diverges from
 *   `shared/crypto/master-key.js` in TWO independent ways — a missing
 *   `oyster:master-key:v1:` domain separator, and p=4 vs p=1 — so delivering
 *   it hands the browser a healthy-LOOKING 32-byte key that decrypts nothing.
 *   The password path needs no such key at all.
 *   Pinned by `__tests__/auth/t901-d5-authenticated-password-change.test.js`
 *   D1/b and by the sibling divergence guard's P3/a + P3/b.
 *
 * ── ONE RE-WRAP, TWO GATES (the "unused twin" failure mode, avoided) ────────
 * `rewrapIdentityMasterUnderPassword` is the SINGLE implementation of the
 * re-wrap tail. Both entry points call it:
 *
 *   mnemonic gate  → `recovery-client.buildPasswordReset`   (forgot password)
 *   password gate  → `buildPasswordChange` below            (change password)
 *
 * Writing the tail twice would create exactly the shape this repo keeps
 * getting bitten by: two implementations of one operation, one of which
 * quietly drifts. `D1/g` measures that they agree.
 *
 * ── CUSTODY (CLAUDE.md §Legal Foundation) ───────────────────────────────────
 * Argon2id runs HERE, in the browser. The server receives only
 * `{ currentPasswordProof, newSaltPw, newWrapPw, newPasswordProof }` — an
 * opaque wrap plus two one-way proofs. It never sees the password and never
 * sees the unwrapped `identityMaster`.
 *
 * Pure functions, no I/O. Browser-safe (only @noble/* via kdf/symmetric).
 */

const crypto = require('crypto');

const { argon2id, KDF_PROFILES } = require('./kdf');
const symmetric = require('./symmetric');
const { deriveAuthProof } = require('./auth-proof');
const { unlockIdentityVault } = require('./login-client');

/**
 * Re-wrap an ALREADY-UNWRAPPED `identityMaster` under a new password.
 *
 * Callers must have opened the vault first — by mnemonic (recovery) or by the
 * current password (change). This function performs no authorization of its
 * own; the gate belongs to the caller, and each caller has a different one.
 *
 * @param {object} args
 * @param {Uint8Array|Buffer} args.identityMaster — the stable inner content key
 * @param {string} args.newPassword
 * @param {object} [args.kdfProfile] — Argon2id parameters (defaults to DESKTOP)
 * @returns {{ newSaltPw: string, newWrapPw: object, newPasswordProof: string }}
 */
function rewrapIdentityMasterUnderPassword({ identityMaster, newPassword, kdfProfile } = {}) {
  if (typeof newPassword !== 'string' || newPassword.length === 0) {
    throw new Error('Invalid newPassword: must be a non-empty string');
  }
  if (!identityMaster || identityMaster.length !== symmetric.KEY_LENGTH) {
    throw new Error('Invalid identityMaster: must be ' + symmetric.KEY_LENGTH + ' bytes');
  }
  const profile = kdfProfile || KDF_PROFILES.DESKTOP;

  // A FRESH salt every time. Reusing the old `saltPw` would leave the new wrap
  // and any captured old wrap sharing a KDF salt for the same account.
  const newSaltPwBytes = crypto.randomBytes(16);
  const newKeyPw = argon2id(newPassword, newSaltPwBytes, profile);
  const newWrapPw = symmetric.encrypt(identityMaster, newKeyPw);
  const newSaltPw = Buffer.from(newSaltPwBytes).toString('hex');

  // T207: the NEW auth verifier is derived client-side, so the server can
  // update its stored verifier without ever holding the raw password.
  const newPasswordProof = deriveAuthProof({
    password: newPassword,
    saltPw: newSaltPw,
    kdfProfile: profile,
  });

  return { newSaltPw, newWrapPw, newPasswordProof };
}

/**
 * Build the wire payload for an AUTHENTICATED "change my password". The
 * browser:
 *   1. Unwraps the existing `identityMaster` with the CURRENT password
 *      (the shipped `unlockIdentityVault` — the same function login uses,
 *      so a wrong password fails here on the AES-GCM auth tag)
 *   2. Derives the current-password auth proof over the CURRENT `saltPw`
 *      (the value `POST /auth/vault/login` already verifies), so the server
 *      can re-authenticate the act without receiving the password
 *   3. Re-wraps the SAME `identityMaster` under the new password
 *
 * The unwrap in step 1 is a real gate, not a formality: it is what makes a
 * caller holding only a stolen session cookie unable to produce a payload.
 * The server checks the proof as well — one gate in the browser is a claim
 * about the client, which is not a control.
 *
 * @returns {{
 *   currentPasswordProof: string,
 *   newSaltPw: string,
 *   newWrapPw: object,
 *   newPasswordProof: string
 * }}
 */
function buildPasswordChange({ currentPassword, newPassword, meta, vaultBin, kdfProfile } = {}) {
  if (typeof currentPassword !== 'string' || currentPassword.length === 0) {
    throw new Error('Invalid currentPassword: must be a non-empty string');
  }
  if (typeof newPassword !== 'string' || newPassword.length === 0) {
    throw new Error('Invalid newPassword: must be a non-empty string');
  }
  const opened = unlockIdentityVault({ password: currentPassword, meta, vaultBin, kdfProfile });

  const currentPasswordProof = deriveAuthProof({
    password: currentPassword,
    saltPw: meta.saltPw,
    kdfProfile: kdfProfile || KDF_PROFILES.DESKTOP,
  });

  const rewrapped = rewrapIdentityMasterUnderPassword({
    identityMaster: opened._identityMaster,
    newPassword,
    kdfProfile,
  });

  return { currentPasswordProof, ...rewrapped };
}

module.exports = {
  rewrapIdentityMasterUnderPassword,
  buildPasswordChange,
};
